import type {
  IErrorResponse,
  IOAuthErrorResponse,
} from '@manager/api/types/general-types';
import { useNotifications } from '@manager/modules/snackbar/composables/use-notifications';
import { i18n } from '@manager/plugins/i18n-plugin';
import { useAuthStore } from '@manager/store/auth-store';
import { isMigrationBlockedError } from '@manager/utils/error-utils';
import { refreshAuthUser } from '@manager/utils/migration-block-utils';
import { getPersistedLanguage } from '@manager/utils/user-settings-utils';
import { captureException } from '@sentry/vue';
import {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  create,
  type InternalAxiosRequestConfig,
  isAxiosError as nativeAxiosError,
} from 'axios';
import memoize from 'memoize';
import { createTrustedOriginChecker, safeOrigin } from 'shared/utils';

const status = {
  unauthorized: 401,
};

export function isAxiosError(e: unknown): e is AxiosError {
  return nativeAxiosError(e);
}

// Parallel requests all fail with the same 403, so toast once and refetch the
// flag so the banner appears even if the block started mid-session.
const notifyMigrationBlocked = memoize(
  () => {
    useNotifications().showError(i18n.global.t('migrationBlock.snackbar'));
    refreshAuthUser();
  },
  { maxAge: 5000 }
);

export function isErrorResponse(e: unknown): e is AxiosError<IErrorResponse> {
  return (
    isAxiosError(e) &&
    (e.response?.data as IErrorResponse)?.error?.details != null
  );
}

export function isOAuthError(e: unknown): e is AxiosError<IOAuthErrorResponse> {
  return (
    isErrorResponse(e) &&
    (e.response?.data as IOAuthErrorResponse)?.error?.code ===
      'general.unauthorized'
  );
}

const trustedOrigins = new Set(
  [
    safeOrigin(import.meta.env.VITE_APP_API_URL),
    safeOrigin(import.meta.env.VITE_APP_BILLING_API_URL),
  ].filter((origin): origin is string => origin !== null)
);

const isTrustedKinnovisRequest = createTrustedOriginChecker(trustedOrigins);

export function requestInterceptor<T extends AxiosRequestConfig>(config: T) {
  if (isTrustedKinnovisRequest(config)) {
    const authStore = useAuthStore();
    const token = authStore.getToken;
    // Always send Content-Language so public/report routes get correct translations.
    const resolvedLocale =
      (i18n.global.locale as { value?: string }).value ||
      i18n.global.locale ||
      getPersistedLanguage();
    const contentLanguage = String(resolvedLocale).toLowerCase();

    config.headers = {
      ...config.headers,
      'Content-Language': contentLanguage,
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    };
  }

  return config;
}

export async function responseSuccessInterceptor(response: AxiosResponse) {
  return response.data;
}

export function refreshToken(): Promise<unknown> {
  const authStore = useAuthStore();
  if (authStore.hasRefreshToken) {
    try {
      return authStore
        .refreshToken()
        .then(() => {
          return true;
        })
        .catch(() => {
          return authStore.logout({ saveCurrentUrl: true }).then(() => {
            return false;
          });
        });
    } catch {
      return authStore.logout({ saveCurrentUrl: true }).then(() => {
        return false;
      });
    }
  } else {
    return authStore.logout({ saveCurrentUrl: true }).then(() => {
      return false;
    });
  }
}

export const defaultHeaders = {
  'Content-Type': 'application/json',
  'X-Requested-With': 'XMLHttpRequest',
};

export const sharedConfig = {
  baseURL: import.meta.env.VITE_APP_API_URL,
  headers: defaultHeaders,
};

const billingSharedConfig = {
  // The env var is a bare origin — every billing-api route lives under `/api`.
  baseURL: `${import.meta.env.VITE_APP_BILLING_API_URL.replace(/\/+$/, '')}/api`,
  headers: defaultHeaders,
};

const maxAge = 5000;
export const memoizedRefreshToken = memoize(refreshToken, {
  maxAge,
});

function makeErrorInterceptor(client: AxiosInstance) {
  return async function errorInterceptor(error: AxiosError) {
    const config = error.config as InternalAxiosRequestConfig & {
      sent?: boolean;
    };

    const url: string = error.request?.responseURL || '';

    if (url.endsWith('/oauth/token') || url.endsWith('/api/check')) {
      // skipping error interceptor for login/refresh
      throw error;
    }

    const authStore = useAuthStore();

    if (
      isAxiosError(error) &&
      error.response?.status === status.unauthorized &&
      !config?.sent &&
      !authStore.loggedInAsAdmin
    ) {
      config.sent = true;
      // only retry if refresh token returns true - in this case the refresh token worked
      const doRetry = await memoizedRefreshToken();
      if (doRetry) {
        return client(requestInterceptor(config));
      }
      throw error;
    } else {
      if (isMigrationBlockedError(error)) {
        notifyMigrationBlocked();
        throw error;
      }

      if (isErrorResponse(error)) {
        if (
          isOAuthError(error) &&
          (error.response?.data?.error?.details?.error_message ===
            'The refresh token is invalid.' ||
            error.response?.status === status.unauthorized)
        ) {
          return await authStore.logout({ saveCurrentUrl: true });
        } else {
          captureException(error);
        }
      }

      throw error;
    }
  };
}

export const httpClient = create(sharedConfig);
httpClient.interceptors.request.use(requestInterceptor);
httpClient.interceptors.response.use(
  responseSuccessInterceptor,
  makeErrorInterceptor(httpClient)
);

export const kinnovisBillingHttpClient = create(billingSharedConfig);
kinnovisBillingHttpClient.interceptors.request.use(requestInterceptor);
kinnovisBillingHttpClient.interceptors.response.use(
  responseSuccessInterceptor,
  makeErrorInterceptor(kinnovisBillingHttpClient)
);
